import {FC} from 'react'
import styled from 'styled-components'

import {CardUiState, groupCode} from '../../api/promo-codes/card-state'
import {PromoCardData, PromoVenue} from '../../api/promo-codes/types'
import {theme} from '../../theme/theme'
import {useLocale} from '../../utils/i18n'

type CardFields = Pick<
  PromoCardData,
  'balance' | 'code' | 'color' | 'expiryText' | 'kind' | 'line' | 'offerText' | 'title'
> & {
  venue: PromoVenue
}

const Card = styled.div<{tint: string}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 16px;
  color: ${theme.colors['text-contrast']};
  background: ${(props) => props.tint};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const Offer = styled.div`
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
`

const Pill = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
`

const Footer = styled(Row)`
  padding-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.4);
  font-size: 13px;
`

const CodeTag = styled.span<{tint: string}>`
  padding: 3px 8px;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: ${theme.colors.white};
  color: ${(props) => props.tint};
`

const STAMP: Record<
  Exclude<CardUiState, 'ready'>,
  'promo_card_expired' | 'promo_card_paused' | 'promo_card_soon' | 'promo_card_used'
> = {
  used: 'promo_card_used',
  expired: 'promo_card_expired',
  paused: 'promo_card_paused',
  soon: 'promo_card_soon',
}

// The venue's own colour is data from the server; a spent card goes grey.
export const PromoCard: FC<{card: CardFields; state?: CardUiState; warn?: boolean; hideCode?: boolean}> = ({
  card,
  state = 'ready',
  warn = false,
  hideCode = false,
}) => {
  const {l} = useLocale()
  const spent = state !== 'ready'
  const tint = spent ? theme.colors['text-secondary'] : card.color || card.venue.color || theme.colors.primary
  const code = card.code && !spent && !hideCode ? groupCode(card.code) : null
  const isVoucher = (card.kind ?? '').toLowerCase() === 'voucher'
  return (
    <Card tint={tint}>
      <Row>
        <span>{card.venue.name}</span>
        <Pill>{l(isVoucher ? 'promo_card_voucher' : 'promo_card_code')}</Pill>
      </Row>
      {card.title && <strong>{card.title}</strong>}
      <Offer>{card.offerText}</Offer>
      {card.line && <span>{card.line}</span>}
      {card.balance && <span>{card.balance}</span>}
      <Footer>
        <span style={warn && !spent ? {color: theme.colors['bg-expiring'], fontWeight: 600} : undefined}>
          {card.expiryText}
        </span>
        {code && <CodeTag tint={tint}>{code}</CodeTag>}
        {spent && <CodeTag tint={tint}>{l(STAMP[state])}</CodeTag>}
      </Footer>
    </Card>
  )
}

export const TicketIcon: FC<{size?: number}> = ({size = 20}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <path d='M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z' />
    <path d='M13 5v2M13 11v2M13 17v2' />
  </svg>
)

const Ghost = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 20px 24px;
  border-radius: 16px;
  border: 1.5px dashed ${theme.colors.primary};
  background: ${theme.colors['sport-bg']};
  text-align: center;
`

// The ticket's side notches: half circles cut into the dashed edge.
const Notch = styled.span<{side: 'left' | 'right'}>`
  position: absolute;
  top: 50%;
  ${(props) => props.side}: -11px;
  width: 20px;
  height: 20px;
  margin-top: -10px;
  border-radius: 50%;
  border: 1.5px dashed ${theme.colors.primary};
  background: ${theme.colors.bg};
  clip-path: ${(props) => (props.side === 'left' ? 'inset(0 0 0 50%)' : 'inset(0 50% 0 0)')};
`

const GhostIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 6px;
  border-radius: 50%;
  color: ${theme.colors['text-contrast']};
  background: ${theme.colors.primary};
`

const GhostTitle = styled.strong`
  font-size: 16px;
  color: ${theme.colors['text-default']};
`

const GhostBody = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors['text-secondary']};
`

// Empty state: a dashed outline of a promotion card, so the customer sees what a code will look like.
export const GhostPromoCard: FC<{title: string; body: string}> = ({title, body}) => (
  <Ghost>
    <Notch side='left' />
    <Notch side='right' />
    <GhostIcon>
      <TicketIcon size={22} />
    </GhostIcon>
    <GhostTitle>{title}</GhostTitle>
    <GhostBody>{body}</GhostBody>
  </Ghost>
)
